import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Users, Beaker } from 'lucide-react';
import { getImageUrl } from '../lib/utils';

const CollegeDetail = () => {
  const { id } = useParams();
  const [college, setCollege] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/colleges/${id}`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/programs/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/faculty/`).then(res => res.json()).catch(() => []),
      fetch(`${BACKEND_URL}/api/labs/`).then(res => res.json()).catch(() => []),
    ])
      .then(([collegeData, programsData, facultyData, labsData]) => {
        setCollege(collegeData);
        // Filter programs by college
        setPrograms(programsData.filter(p => p.college_id === id));
        setFaculty(facultyData.filter(f => f.college_id === id));
        setLabs(labsData.filter(l => l.college_id === id));
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));
  }, [id, BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F388E] mb-4">الكلية غير موجودة</h2>
          <Link to="/colleges" className="text-[#C9A040] hover:underline">
            العودة إلى قائمة الكليات
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-[420px] pt-32 pb-20 flex items-center justify-center overflow-hidden"
        style={{
          paddingTop: '130px',
          backgroundImage: college.cover_image ? `url(${getImageUrl(college.cover_image)})` : 'none',
          backgroundColor: college.cover_image ? 'transparent' : '#0F388E'
        }}
      >
        {college.cover_image && (
          <div className="absolute inset-0 bg-[#0F388E]/70"></div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          {college.image && (
            <div className="mb-6">
              <img 
                src={getImageUrl(college.image)} 
                alt={college.name_ar} 
                className="h-24 w-auto mx-auto object-contain drop-shadow-2xl"
              />
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{college.name_ar}</h1>
          {college.name_en && (
            <p className="text-xl text-white/90">{college.name_en}</p>
          )}
          {college.description_ar && (
            <p className="text-lg text-white/80 mt-4 max-w-3xl mx-auto">
              {college.description_ar}
            </p>
          )}
        </div>
      </section>

      {/* About Section */}
      {college.about_ar && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-6">نبذة عن الكلية</h2>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            <div 
              className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: college.about_ar }}
            />
          </div>
        </section>
      )}

      {/* Vision, Mission, Objectives */}
      {(college.vision_ar || college.mission_ar || college.objectives_ar) && (
        <section className="py-16 bg-gray-50 dark:bg-[#0B1A30]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {college.vision_ar && (
                <div className="bg-white dark:bg-[#0E2243] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#0F388E] dark:border-[#C9A040] dark:border-x dark:border-b dark:border-gray-800">
                  <div className="text-[#C9A040] text-4xl mb-4">🎯</div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الرؤية</h3>
                  <div 
                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: college.vision_ar }}
                  />
                </div>
              )}
              {college.mission_ar && (
                <div className="bg-white dark:bg-[#0E2243] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#C9A040] dark:border-x dark:border-b dark:border-gray-800">
                  <div className="text-[#C9A040] text-4xl mb-4">🎓</div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الرسالة</h3>
                  <div 
                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: college.mission_ar }}
                  />
                </div>
              )}
              {college.objectives_ar && (
                <div className="bg-white dark:bg-[#0E2243] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#0F388E] dark:border-[#C9A040] dark:border-x dark:border-b dark:border-gray-800">
                  <div className="text-[#C9A040] text-4xl mb-4">📋</div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-4">الأهداف</h3>
                  <div 
                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: college.objectives_ar }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Dean's Message */}
      {college.dean_message_ar && (
        <section className="py-16 bg-white dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-8 text-center">كلمة عميد الكلية</h2>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-[#0E2243] dark:to-[#0A1A33] rounded-2xl p-8 md:p-12 shadow-lg dark:border dark:border-gray-800">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                {college.dean_image && (
                  <div className="flex-shrink-0">
                    <img 
                      src={getImageUrl(college.dean_image)} 
                      alt={college.dean_name_ar} 
                      className="w-32 h-32 md:w-48 md:h-48 rounded-full object-cover shadow-xl border-4 border-white dark:border-gray-700"
                    />
                  </div>
                )}
                <div className="flex-1">
                  {college.dean_name_ar && (
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white">{college.dean_name_ar}</h3>
                      {college.dean_title_ar && (
                        <p className="text-[#C9A040] font-medium">{college.dean_title_ar}</p>
                      )}
                    </div>
                  )}
                  <div 
                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: college.dean_message_ar }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Programs */}
      {programs.length > 0 && (
        <section className="py-16 bg-gray-50 dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <GraduationCap className="w-8 h-8 text-[#C9A040]" />
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white">البرامج الأكاديمية</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map(program => (
                <Link 
                  key={program.id}
                  to={`/programs/${program.id}`}
                  className="bg-white dark:bg-[#0E2243] rounded-xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 dark:border-gray-800"
                >
                  {program.image && (
                    <img 
                      src={program.image} 
                      alt={program.title_ar} 
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                  )}
                  <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-2">{program.title_ar}</h3>
                  {program.degree && (
                    <p className="text-sm text-[#C9A040] mb-2">{program.degree}</p>
                  )}
                  {program.duration && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">المدة: {program.duration}</p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Labs */}
      {labs.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-[#071326] dark:to-[#0B1A30]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <Beaker className="w-10 h-10 text-[#C9A040]" />
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white">معامل الكلية</h2>
            </div>
            <div className="w-24 h-1 bg-[#C9A040] mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {labs.map(lab => (
                <div 
                  key={lab.id}
                  className="group bg-white dark:bg-[#0E2243] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-800"
                >
                  {/* Lab Image */}
                  <div className="relative overflow-hidden">
                    {lab.image ? (
                      <img 
                        src={getImageUrl(lab.image)} 
                        alt={lab.name_ar} 
                        className="w-full h-56 object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-56 bg-gradient-to-br from-[#0F388E] to-[#1d5bb8] flex items-center justify-center">
                        <Beaker className="w-20 h-20 text-white/30" />
                      </div>
                    )}
                    {/* Overlay Effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  {/* Lab Content */}
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#C9A040]/10 rounded-full flex items-center justify-center">
                        <Beaker className="w-5 h-5 text-[#C9A040]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0F388E] dark:text-white leading-tight group-hover:text-[#C9A040] transition-colors">
                        {lab.name_ar}
                      </h3>
                    </div>
                    
                    {lab.description_ar && (
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-4 mb-4">
                        {lab.description_ar}
                      </p>
                    )}
                    
                    {/* Additional Lab Details */}
                    {(lab.capacity || lab.supervisor_name_ar || lab.location) && (
                      <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                        {lab.capacity && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="text-[#C9A040]">👥</span>
                            <span>السعة: {lab.capacity} طالب</span>
                          </div>
                        )}
                        {lab.supervisor_name_ar && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="text-[#C9A040]">👨‍🔬</span>
                            <span>المشرف: {lab.supervisor_name_ar}</span>
                          </div>
                        )}
                        {lab.location && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="text-[#C9A040]">📍</span>
                            <span>الموقع: {lab.location}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Faculty */}
      {faculty.length > 0 && (
        <section className="py-16 bg-gray-50 dark:bg-[#071326]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Users className="w-8 h-8 text-[#C9A040]" />
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white">كادر الكلية</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {faculty.map(member => (
                <div 
                  key={member.id}
                  className="bg-white dark:bg-[#0E2243] rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow border border-gray-100 dark:border-gray-800"
                >
                  {member.image && (
                    <img 
                      src={getImageUrl(member.image)} 
                      alt={member.name_ar} 
                      className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-gray-100 dark:border-gray-700"
                    />
                  )}
                  <h3 className="text-lg font-bold text-[#0F388E] dark:text-white mb-1">{member.name_ar}</h3>
                  {member.title_ar && (
                    <p className="text-sm text-[#C9A040] mb-2">{member.title_ar}</p>
                  )}
                  {member.specialization_ar && (
                    <p className="text-xs text-gray-600 dark:text-gray-300">{member.specialization_ar}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back Button */}
      <div className="py-8 text-center">
        <Link 
          to="/colleges" 
          className="inline-flex items-center gap-2 text-[#0F388E] dark:text-[#C9A040] hover:text-[#C9A040] dark:hover:text-white font-medium"
        >
          <ArrowRight className="w-5 h-5" />
          العودة إلى الكليات
        </Link>
      </div>
    </div>
  );
};

export default CollegeDetail;
